import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {of} from 'rxjs';
import {BackgroundTaskService} from './background-task.service';
import {BackgroundTaskActions} from './background-task.actions';
import {BackgroundTaskSelectors} from './background-task.selectors';
import {BackgroundTaskStatus} from './background-task.model';

const DEFAULT_TASK_LIMIT = 10;

@Injectable()
export class BackgroundTaskEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private backgroundTaskService: BackgroundTaskService
  ) {}

  loadPendingTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BackgroundTaskActions.loadPendingTasks),
      withLatestFrom(this.store.select(BackgroundTaskSelectors.selectPendingTasks)),
      switchMap(([_, previousTasks]) =>
        this.backgroundTaskService.getPendingTasks().pipe(
          switchMap((tasks) => {
            const actions: any[] = [BackgroundTaskActions.loadPendingTasksSuccess({tasks})];

            previousTasks.forEach(prevTask => {
              const currentTask = tasks.find(t => t.id === prevTask.id);
              if (!currentTask ||
                  currentTask.status === BackgroundTaskStatus.Completed ||
                  currentTask.status === BackgroundTaskStatus.Failed) {
                const completedTask = currentTask || {...prevTask, status: BackgroundTaskStatus.Completed};
                actions.push(BackgroundTaskActions.taskCompleted({task: completedTask}));
              }
            });

            return actions;
          }),
          catchError((error) =>
            of(BackgroundTaskActions.loadPendingTasksFailure({error}))
          )
        )
      )
    )
  );

  loadTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BackgroundTaskActions.loadTasks),
      switchMap(({limit}) =>
        this.backgroundTaskService.getTasks(limit || DEFAULT_TASK_LIMIT).pipe(
          map((tasks) => BackgroundTaskActions.loadTasksSuccess({tasks})),
          catchError((error) =>
            of(BackgroundTaskActions.loadTasksFailure({error}))
          )
        )
      )
    )
  );
}
